/**
 * Skeletons del storefront tenant — pending de loaders + placeholders de UI.
 * Sin branding del tenant (aún no hay loaderData); fondo claro neutro para
 * evitar el flash oscuro que ocurre cuando la app está en dark mode pero
 * el storefront usa light palette.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

// Bloque de color pulsante neutro — no depende de CSS vars del tema de la app
function Bone({ className }: { className?: string }) {
  return (
    <div
      className={cn("animate-pulse rounded-md bg-slate-200/80", className)}
      aria-hidden="true"
    />
  );
}

function ChromeSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("border-b border-slate-200 bg-white", className)}>
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4 sm:h-16 sm:gap-4">
        <Bone className="h-8 w-8 shrink-0 rounded-full sm:h-9 sm:w-9" />
        <Bone className="hidden h-4 w-28 sm:block" />
        <Bone className="ml-auto h-9 flex-1 max-w-md rounded-full" />
        <Bone className="h-9 w-9 shrink-0 rounded-full" />
        <Bone className="h-9 w-9 shrink-0 rounded-full" />
      </div>
      <div className="mx-auto hidden max-w-7xl items-center gap-4 px-4 pb-3 md:flex">
        {Array.from({ length: 5 }).map((_, i) => (
          <Bone key={i} className="h-3 w-16" />
        ))}
      </div>
    </div>
  );
}

function ProductCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <Bone className="aspect-square w-full rounded-none" />
      <div className="space-y-2 p-3">
        <Bone className="h-3 w-2/3" />
        <Bone className="h-3 w-1/3" />
        <Bone className="mt-2 h-8 w-full" />
      </div>
    </div>
  );
}

function ProductGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-5">
      {Array.from({ length: count }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  );
}

function Shell({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn("min-h-svh bg-[#f8fafc]", className)}
      role="status"
      aria-busy="true"
      aria-label="Cargando tienda"
    >
      <ChromeSkeleton />
      {children}
    </div>
  );
}

/** Home / landing del tenant. */
export function MarketHomeSkeleton() {
  return (
    <Shell>
      <Bone className="h-[42svh] w-full rounded-none sm:h-[52svh]" />
      <div className="mx-auto max-w-7xl space-y-8 px-4 py-6 sm:py-10">
        <div className="flex gap-3 overflow-hidden">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="w-20 shrink-0 space-y-2 sm:w-24">
              <Bone className="mx-auto h-14 w-14 rounded-full sm:h-16 sm:w-16" />
              <Bone className="mx-auto h-2.5 w-14" />
            </div>
          ))}
        </div>
        <div className="space-y-3">
          <Bone className="h-5 w-40" />
          <div className="flex gap-3 overflow-hidden">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="w-40 shrink-0 sm:w-48">
                <ProductCardSkeleton />
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-3">
          <Bone className="h-5 w-36" />
          <ProductGridSkeleton count={6} />
        </div>
      </div>
    </Shell>
  );
}

/** PLP / catálogo / categoría / marca / colección. */
export function MarketPlpSkeleton() {
  return (
    <Shell>
      <div className="mx-auto max-w-7xl px-4 py-6 sm:py-8">
        <Bone className="mb-4 h-8 w-48 sm:h-10 sm:w-64" />
        <Bone className="mb-6 h-4 w-full max-w-lg" />
        <div className="mb-6 flex flex-wrap gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Bone key={i} className="h-8 w-20 rounded-full" />
          ))}
        </div>
        <div className="flex gap-6">
          <div className="hidden w-56 shrink-0 space-y-3 lg:block">
            {Array.from({ length: 8 }).map((_, i) => (
              <Bone key={i} className="h-4 w-full" />
            ))}
          </div>
          <div className="min-w-0 flex-1">
            <ProductGridSkeleton count={10} />
          </div>
        </div>
      </div>
    </Shell>
  );
}

/** PDP / combo / regalo. */
export function MarketPdpSkeleton() {
  return (
    <Shell>
      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-6 sm:py-10 lg:grid-cols-2">
        <div className="space-y-3">
          <Bone className="aspect-square w-full rounded-xl" />
          <div className="flex gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Bone key={i} className="h-16 w-16 rounded-md" />
            ))}
          </div>
        </div>
        <div className="space-y-4 pt-2">
          <Bone className="h-3 w-24" />
          <Bone className="h-8 w-4/5" />
          <Bone className="h-6 w-28" />
          <Bone className="h-20 w-full" />
          <div className="flex gap-3 pt-2">
            <Bone className="h-11 flex-1 rounded-md" />
            <Bone className="h-11 w-11 rounded-md" />
          </div>
          <Bone className="h-24 w-full rounded-xl" />
        </div>
      </div>
    </Shell>
  );
}

/** Checkout. */
export function MarketCheckoutSkeleton() {
  return (
    <Shell>
      <div className="mx-auto grid max-w-5xl gap-8 px-4 py-8 lg:grid-cols-[1fr_340px]">
        <div className="space-y-4">
          <Bone className="h-7 w-40" />
          {Array.from({ length: 6 }).map((_, i) => (
            <Bone key={i} className="h-11 w-full rounded-md" />
          ))}
          <Bone className="h-28 w-full rounded-xl" />
        </div>
        <div className="space-y-3 rounded-xl border border-slate-200 p-4">
          <Bone className="h-5 w-32" />
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex gap-3">
              <Bone className="h-14 w-14 rounded-md" />
              <div className="flex-1 space-y-2">
                <Bone className="h-3 w-3/4" />
                <Bone className="h-3 w-1/3" />
              </div>
            </div>
          ))}
          <Bone className="mt-4 h-11 w-full rounded-md" />
        </div>
      </div>
    </Shell>
  );
}

/** Packs / bundle builder. */
export function MarketBundleSkeleton() {
  return (
    <Shell>
      <div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
        <Bone className="h-8 w-56" />
        <Bone className="h-4 w-full max-w-md" />
        <div className="flex gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Bone key={i} className="h-2 flex-1 rounded-full" />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="overflow-hidden rounded-xl border border-slate-200">
              <Bone className="aspect-[4/5] w-full rounded-none" />
              <div className="space-y-2 p-3">
                <Bone className="h-4 w-2/3" />
                <Bone className="h-3 w-1/3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}

/** Página genérica (cuenta, reservar, eventos, static, etc.). */
export function MarketPageSkeleton() {
  return (
    <Shell>
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-10">
        <Bone className="h-8 w-48" />
        <Bone className="h-4 w-full max-w-lg" />
        <Bone className="h-40 w-full rounded-xl" />
        <Bone className="h-24 w-full rounded-xl" />
        <Bone className="h-24 w-full rounded-xl" />
      </div>
    </Shell>
  );
}

export {
  ChromeSkeleton as MarketChromeSkeleton,
  ProductCardSkeleton as MarketProductCardSkeleton,
  ProductGridSkeleton as MarketProductGridSkeleton,
};
